/**
 * Environment Variable Validation
 * 
 * This module validates that all required environment variables are set.
 * It runs at app startup and throws a clear error if any are missing.
 */

const requiredEnvVars = [
  {
    name: 'REACT_APP_BACKEND_URL',
    description: 'Backend API URL',
    example: 'https://your-backend.vercel.app'
  },
  {
    name: 'REACT_APP_FIREBASE_API_KEY',
    description: 'Firebase API Key',
    example: 'AIzaSy...'
  },
  {
    name: 'REACT_APP_FIREBASE_AUTH_DOMAIN',
    description: 'Firebase Auth Domain',
    example: 'your-project.firebaseapp.com'
  },
  {
    name: 'REACT_APP_FIREBASE_PROJECT_ID',
    description: 'Firebase Project ID',
    example: 'your-project-id'
  }
];

const optionalEnvVars = [
  {
    name: 'REACT_APP_GOOGLE_MAPS_API_KEY',
    description: 'Google Maps API Key (for location features)',
    example: 'AIzaSy...'
  },
  {
    name: 'REACT_APP_OPENWEATHER_API_KEY',
    description: 'OpenWeather API Key (for weather display)',
    example: 'your-openweather-key'
  },
  {
    name: 'REACT_APP_FIREBASE_STORAGE_BUCKET',
    description: 'Firebase Storage Bucket',
    example: 'your-project.appspot.com'
  },
  {
    name: 'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    description: 'Firebase Messaging Sender ID (for push notifications)',
    example: '123456789'
  },
  {
    name: 'REACT_APP_FIREBASE_APP_ID',
    description: 'Firebase App ID (for analytics)',
    example: '1:123456789:web:abc123'
  }
];

/**
 * Validate all required environment variables
 * @returns {Object} Validation result with missing and warnings
 */
export const validateEnvironment = () => {
  const missing = [];
  const warnings = [];

  // Check required variables
  requiredEnvVars.forEach(envVar => {
    const value = process.env[envVar.name];
    if (!value || value.trim() === '') {
      missing.push(envVar);
    }
  });

  // Check optional variables (warn if missing)
  optionalEnvVars.forEach(envVar => {
    const value = process.env[envVar.name];
    if (!value || value.trim() === '') {
      warnings.push(envVar);
    }
  });

  return { missing, warnings, isValid: missing.length === 0 };
};

/**
 * Run validation and log results
 * In production, throws error if required vars are missing
 * In development, logs warnings
 */
export const checkEnvironment = () => {
  const { missing, warnings, isValid } = validateEnvironment();
  const isDevelopment = process.env.NODE_ENV !== 'production';

  // Log warnings for missing optional variables
  if (warnings.length > 0 && isDevelopment) {
    console.warn('⚠️ Optional environment variables not set:');
    warnings.forEach(v => {
      console.warn(`   - ${v.name}: ${v.description}`);
    });
    console.warn('   These features may not work properly.\n');
  }

  // Handle missing required variables
  if (!isValid) {
    const missingDetails = missing.map(v => 
      `   ${v.name}\n      ${v.description}\n      Example: ${v.example}`
    ).join('\n\n');

    const errorMessage = [
      '❌ Missing required environment variables:',
      '',
      missingDetails,
      '',
      '📝 Create a .env file in the frontend folder with these variables.',
      '   See .env.example for a template.',
      ''
    ].join('\n');

    if (process.env.NODE_ENV === 'production') {
      // In production, throw an error to prevent app from running with missing config
      throw new Error(errorMessage);
    } else {
      // In development, log error but allow app to continue
      console.error(errorMessage);
    }
  } else if (isDevelopment) {
    console.log('✅ All required environment variables are set');
  }

  return isValid;
};

/**
 * Get all environment config (for debugging)
 * Masks sensitive values
 */
export const getEnvConfig = () => {
  const config = {};
  
  [...requiredEnvVars, ...optionalEnvVars].forEach(envVar => {
    const value = process.env[envVar.name];
    if (value) {
      // Mask sensitive values (show first 4 and last 4 chars)
      if (envVar.name.includes('KEY') || envVar.name.includes('SECRET')) {
        config[envVar.name] = value.length > 8 
          ? `${value.substring(0, 4)}...${value.substring(value.length - 4)}`
          : '****';
      } else {
        config[envVar.name] = value;
      }
    } else {
      config[envVar.name] = '(not set)';
    }
  });

  return config;
};

export default { validateEnvironment, checkEnvironment, getEnvConfig };

